import { AxiosError } from "axios";
import { BookingCalendar } from "PFApp/booking/types";
import { useGrowl } from "PFApp/use_growl";
import { Button } from "PFComponents/button";
import { Card } from "PFComponents/card";
import FolderTabs from "PFComponents/tabs/folder_tabs";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useBookingConfigCalendarsInvalidate } from "PFCore/hooks/queries/bookings/booking_config/use_booking_config_calendars_invalidate";
import { useDeleteExceptionDay } from "PFCore/hooks/queries/bookings/booking_config/use_delete_exception_day";
import { useExceptionDaysInvalidate } from "PFCore/hooks/queries/bookings/booking_config/use_exception_days_invalidate";
import { useNominalDaysInvalidate } from "PFCore/hooks/queries/bookings/booking_config/use_nominal_days_invalidate";
import { useUpdateCalendar } from "PFCore/hooks/queries/bookings/booking_config/use_update_calendar";
import { useBookingsFromAvailabilityInvalidate } from "PFCore/hooks/queries/bookings/bookings_from_availability/use_bookings_from_availability_invalidate";
import User from "PFIcons/user.svg";
import { ExceptionDay, NominalDay } from "PFTypes/booking/booking_config";
import React, { useState } from "react";
import { Controller, FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { CalendarForm } from "../calendars.types";
import Assignees from "../components/assignees/assignees";
import BreadCrumbs from "../components/calendars/breadcrumbs";
import { Dates } from "../components/dates/dates";
import { CalendarIcon, EditIcon } from "../components/icons";
import css from "../edit_booking_calendar.module.scss";
import { mergeExceptionDays, mergeNominalDays, sortWeekdaysByMap } from "./edit_booking_calendar.utils";

enum Tabs {
  Dates = "DATES",
  Assignees = "ASSIGNEES"
}

type EditBookingCalendarProps = {
  calendar: BookingCalendar;
  nominalDays: NominalDay[];
  exceptionDays: ExceptionDay[];
};

export const EditBookingCalendar = ({ calendar, nominalDays, exceptionDays }: EditBookingCalendarProps) => {
  const [tabId, setTabId] = useState();
  const methods = useForm<CalendarForm>({
    defaultValues: {
      name: calendar.name,
      nominalDays: sortWeekdaysByMap(nominalDays),
      exceptionDays
    }
  });
  const { t } = useTranslation("bookingModule");
  const growl = useGrowl();

  const { getFirstError } = useResponseErrors();
  const handleError = (error: AxiosError<Error>) => {
    try {
      const errorMessage = getFirstError(error.response?.data);
      growl({
        message: errorMessage,
        kind: "error"
      });
    } catch {
      growl({
        message: t("config.calendars.unknownError"),
        kind: "error"
      });
    }
  };
  const { invalidate: invalidateBookingsFromAvailability } = useBookingsFromAvailabilityInvalidate();
  const { invalidate: invalidateCalendars } = useBookingConfigCalendarsInvalidate();
  const { invalidate: nominalDaysInvalidate } = useNominalDaysInvalidate();
  const { invalidate: exceptionDaysInvalidate } = useExceptionDaysInvalidate();

  const { mutate: updateCalendar } = useUpdateCalendar({
    options: {
      onSuccess: () => {
        nominalDaysInvalidate(calendar.id);
        exceptionDaysInvalidate(calendar.id);
        // TODO: [ENG-2737] Invalidate by profiles if possible when calendars are assigned to profiles
        invalidateBookingsFromAvailability();
        invalidateCalendars();
        growl({
          message: t("config.calendars.newCalendar.updateSuccess"),
          kind: "success"
        });
      },
      onError: handleError
    }
  });

  const { mutate: deleteExceptionDay } = useDeleteExceptionDay({
    options: {
      onSuccess: () => {
        exceptionDaysInvalidate(calendar.id);
      },
      onError: handleError
    }
  });

  const { handleSubmit, getValues } = methods;

  const [editMode, setEditMode] = useState(false);

  const toggleEditMode = () => setEditMode((prevEditMode) => !prevEditMode);

  const onSubmit = (form: CalendarForm) => {
    updateCalendar({
      calendarId: calendar.id,
      calendarName: form.name,
      nominalDays: mergeNominalDays(form.nominalDays, nominalDays, calendar.id),
      exceptionDays: mergeExceptionDays(form.exceptionDays, exceptionDays, calendar.id)
    });
  };
  const handleSave: SubmitHandler<CalendarForm> = (formData) => onSubmit(formData);

  const handleDeleteExceptionDay = (exceptionDay) => {
    deleteExceptionDay({
      calendarId: calendar.id,
      exceptionDayId: exceptionDay.id
    });
  };

  const handleTabChange = (_, id) => setTabId(id);

  return (
    <>
      <BreadCrumbs currentName={getValues("name")} />
      <Card paddingVariant="none" className={css.card}>
        <FormProvider {...methods}>
          {editMode ? (
            <div style={{ display: "flex", alignItems: "center" }}>
              <div className={css.header}>
                <Controller
                  name={"name"}
                  render={({ field }) => (
                    <InputFieldSet {...field} onChange={field.onChange} onBlur={field.onBlur} />
                  )}
                />
              </div>
              <Button kind="secondary" onClick={toggleEditMode}>
                {t("config.calendars.update")}
              </Button>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center" }}>
              <div className={css.header}>{getValues("name")}</div>
              <Button kind="secondary" onClick={toggleEditMode}>
                <EditIcon height={15} width={15} />
              </Button>
            </div>
          )}
          <FolderTabs
            onTabSelected={handleTabChange}
            tabs={[
              {
                id: Tabs.Dates,
                displayElement: (
                  <div className={css.tab}>
                    <div>
                      <CalendarIcon width={20} height={20} />
                    </div>
                    <div> {t("config.calendars.dates")}</div>
                  </div>
                ),
                Content: (
                  <Dates
                    edit
                    handleDeleteExceptionDay={handleDeleteExceptionDay}
                    submitForm={handleSubmit(handleSave)}
                  />
                )
              },
              {
                id: Tabs.Assignees,
                displayElement: (
                  <div className={css.tab}>
                    <div className={css.tabIcon}>
                      <User width={25} height={25} />
                    </div>
                    <div>{t("config.calendars.assignees.title")}</div>
                  </div>
                ),
                Content: <Assignees />
              }
            ]}
            selectedTabId={tabId}
          />
        </FormProvider>
      </Card>
    </>
  );
};
